import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { useClientPagination } from '../../lib/usePagination.js';
import {
  getWorkspaceReturnContext,
  prependWorkspaceBreadcrumb,
} from '../../lib/workspaceContext.js';
import {
  PageActionStrip,
  Breadcrumbs,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  DataSourceBadge,
  StatusChip,
  FormField,
  Textarea,
  FinancialWaterfall,
  InvestmentDonut,
  KpiCard,
  AlertCard,
  PageViewerControls,
  toast,
  activityRail,
  KpiHeroBand,
  StatusDonutMini,
  InlineScoreCell,
  TrendBars,
  BrandedEmptyState,
} from '../../components/index.js';
import { ErrorZone } from '../../components/primitives/ErrorZone.jsx';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { AdjustForecastDrawer } from './AdjustForecastDrawer.jsx';
import { format, rbac } from '@dha-nexus/shared';
import './financials.css';
import './financials.redesign.css';

const STAGE_COLUMNS = [
  { key: 'intake', label: 'Intake', tone: 'blue' },
  { key: 'execution', label: 'Execution', tone: 'sage' },
  { key: 'sustainment', label: 'Sustainment', tone: 'slate' },
];

function envelopeList(payload) {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

function summaryProjectRows(payload) {
  if (Array.isArray(payload?.data?.projectRows)) return payload.data.projectRows;
  if (Array.isArray(payload?.data?.projects)) return payload.data.projects;
  return [];
}

function projectStage(p) {
  const s = String(p.lifecycleStage || p.status || '').toLowerCase();
  if (/sustain|operate/.test(s)) return 'sustainment';
  if (/intake|concept|plan|business/.test(s)) return 'intake';
  return 'execution';
}

// Map a source/connector freshness label to an executive-traffic-light tone.
// current/fresh -> sage; warning/stale-ish -> amber; expired/error -> brick.
function freshnessTone(value) {
  const s = String(value || '').toLowerCase();
  if (/expired|error|fail|critical|missing/.test(s)) return 'brick';
  if (/warn|stale|degrad|partial|delay|aging/.test(s)) return 'amber';
  if (/current|fresh|ok|success|live|recent/.test(s)) return 'sage';
  return 'slate';
}

const FRESH_TONE_VAR = {
  sage: 'var(--status-sage)',
  amber: 'var(--status-amber)',
  brick: 'var(--status-brick)',
  blue: 'var(--status-blue)',
  slate: 'var(--status-slate)',
};

function FinancialConnectorEvidenceList({ integrationStatus, emptyTitle }) {
  if (!integrationStatus.connectors?.length) {
    return <EmptyState title={emptyTitle} />;
  }

  return (
    <ul className="fin-connector-list">
      {integrationStatus.connectors.map((connector) => (
        <li key={connector.connectorId}>
          <div className="row spread">
            <strong>{connector.connectorName}</strong>
            <StatusChip status={connector.latestSyncStatus || connector.status} />
          </div>
          <div className="muted small mono">{connector.connectorId}</div>
          <div className="muted small">
            Latest sync {connector.latestSyncCompletedAt || 'not recorded'} ·{' '}
            {connector.openIssueCount || 0} open issue(s)
          </div>
          {connector.latestLineageBatchId ? (
            <div className="muted small mono">Lineage batch {connector.latestLineageBatchId}</div>
          ) : null}
          {connector.latestFinancialRecords?.length
            ? connector.latestFinancialRecords.map((record) => (
                <div
                  key={`${connector.connectorId}-${record.recordId}`}
                  className="muted small mono"
                >
                  Imported financial record {record.recordId} · {record.lineageRecordCount || 0}{' '}
                  lineage record(s)
                </div>
              ))
            : null}
        </li>
      ))}
    </ul>
  );
}

export function FinancialLifecycleManagerPage() {
  const [params] = useSearchParams();
  const focusProjectId = params.get('focus');
  const navigate = useNavigate();
  const { returnTo, returnLabel } = getWorkspaceReturnContext(params);
  const breadcrumbs = prependWorkspaceBreadcrumb(
    [{ to: '/financials', label: 'Financials' }],
    params,
  );
  const summary = useApi('/financials/summary');
  const portfolio = useApi('/financials/portfolio-summary');
  const allocation = useApi('/financials/investment-allocation');
  const { user, roles = [] } = useCurrentUser();
  const canSeeProjects = rbac.canSeeRoute('/projects', roles);
  const projects = useApi('/projects', { query: { limit: 100 }, skip: !canSeeProjects });
  const scenariosRes = useApi('/scenarios', { query: { limit: 50 } });
  const funding = useApi('/financials/funding-sources');
  const labor = useApi('/financials/labor-costs');
  const integration = useApi('/integrations/fmis-tableau/status');
  const runScenario = useMutation();
  const adjust = useMutation();
  const adjustmentsRes = useApi('/financials/forecast-adjustments', { query: { limit: 30 } });
  const [scenarioResult, setScenarioResult] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustDefaults, setAdjustDefaults] = useState({});
  const accessLimited = Boolean(summary.data?.data?.accessLimited);
  const accessPolicy = useMemo(() => summary.data?.data?.accessPolicy || {}, [summary.data]);
  const sourceNotes = useMemo(() => summary.data?.data?.sourceNotes || [], [summary.data]);

  const summaryVisuals = useMemo(() => summary.data?.data?.visuals || {}, [summary.data]);
  const allocationRows = useMemo(() => allocation.data?.data?.allocation || [], [allocation.data]);
  const summaryScopedProjects = useMemo(() => summaryProjectRows(summary.data), [summary.data]);
  const projectList = useMemo(() => {
    if (canSeeProjects) return projects.data?.data || summaryScopedProjects;
    return summaryScopedProjects;
  }, [canSeeProjects, projects.data, summaryScopedProjects]);
  const scenarioRows = useMemo(() => scenariosRes.data?.data || [], [scenariosRes.data]);
  const fundingSources = useMemo(() => funding.data?.data?.fundingSources || [], [funding.data]);
  const pendingAdjustments = useMemo(() => adjustmentsRes.data?.data || [], [adjustmentsRes.data]);
  const total = useMemo(() => summary.data?.data?.total || {}, [summary.data]);
  const laborSummary = useMemo(() => labor.data?.data?.summary || {}, [labor.data]);
  const integrationStatus = useMemo(() => integration.data?.data || {}, [integration.data]);
  // Burn rate is either the server's figure, a derivation from two real
  // governed totals, or null — never the old fabricated 42% (TT-04/AC-TRUST-2).
  const derivedBurnRate =
    total.actualSpendUsdM && total.plannedBudgetUsdM
      ? Math.round((total.actualSpendUsdM / total.plannedBudgetUsdM) * 100)
      : null;
  const burnRatePercent = total.burnRatePercent ?? derivedBurnRate;

  const waterfallItems = useMemo(() => {
    if (summaryVisuals.waterfall?.steps?.length) {
      return summaryVisuals.waterfall.steps;
    }
    return [
      { label: 'Planned', value: total.plannedBudgetUsdM || 0, type: 'baseline' },
      {
        label: 'Obligated Δ',
        value: (total.obligatedUsdM || 0) - (total.plannedBudgetUsdM || 0),
        type: 'delta',
      },
      {
        label: 'Expended Δ',
        value: (total.actualSpendUsdM || 0) - (total.obligatedUsdM || 0),
        type: 'delta',
      },
      {
        label: 'Forecast at completion Δ',
        value: (total.forecastAtCompletionUsdM || 0) - (total.actualSpendUsdM || 0),
        type: 'delta',
      },
      {
        label: 'Forecast at completion',
        value: total.forecastAtCompletionUsdM || 0,
        type: 'forecast',
      },
    ];
  }, [summaryVisuals.waterfall?.steps, total]);

  const fundingSlices = useMemo(() => {
    return fundingSources
      .map((f) => ({
        id: f.source || f.fundingType,
        name: f.source || f.fundingType,
        value: f.amountUsd || f.plannedUsdM || 0,
        richTooltip: f.richTooltip,
      }))
      .filter((s) => s.value > 0)
      .slice(0, 6);
  }, [fundingSources]);

  const summaryCards = summaryVisuals.kpiCards || [];
  const financialCard = (cardId) => summaryCards.find((card) => card.id === cardId);
  // Scenario runs are only offered against a REAL loaded scenario record — no
  // SCN-CUT-10 fallback id, no hardcoded 15% cut. When absent, the page shows
  // an explicit "No budget-cut scenario loaded" state instead (TT-04).
  const budgetCutScenario =
    scenarioRows.find((scenario) => scenario.scenarioType === 'budget-cut') || null;
  const budgetCutPercentLabel =
    budgetCutScenario?.budgetDeltaPercent != null &&
    !Number.isNaN(Number(budgetCutScenario.budgetDeltaPercent))
      ? Math.abs(Number(budgetCutScenario.budgetDeltaPercent))
      : null;

  const grouped = useMemo(() => {
    const acc = { intake: [], execution: [], sustainment: [] };
    for (const proj of projectList) acc[projectStage(proj)].push(proj);
    return acc;
  }, [projectList]);
  const pendingAdjustmentPager = useClientPagination(pendingAdjustments, {
    initialPageSize: 5,
    resetKey: `pending-adjustments-${pendingAdjustments.length}`,
  });
  const allocationPager = useClientPagination(allocationRows, {
    initialPageSize: 10,
    resetKey: `allocation-${allocationRows.length}`,
  });

  async function runScenarioNow() {
    if (!budgetCutScenario) return;
    try {
      // No client-side cut parameter: the scenario formula engine derives the
      // cut from the loaded scenario record's own budgetDeltaPercent, so every
      // rendered value is a model output of the real record.
      const res = await runScenario.run(() =>
        api.post(`/scenarios/${budgetCutScenario.id}/run`, {}),
      );
      setScenarioResult(res.data || res);
      toast.success({
        title: 'Scenario complete',
        body: `Forecast Δ ${res.data?.forecastDeltaUsdM ?? '—'} M · risk ${res.data?.riskShift || 'unchanged'}`,
      });
      activityRail.push({
        action: 'scenario.run',
        targetType: 'scenario',
        targetId: res.data?.scenarioId || budgetCutScenario.id,
        actorUserId: user?.id || 'unknown',
        summary: 'Budget-cut scenario simulation',
      });
    } catch (e) {
      toast.error({ title: 'Scenario failed', body: e.message });
    }
  }

  function openAdjustForProject(projectId) {
    setAdjustDefaults({ projectId });
    setAdjustOpen(true);
  }

  async function handleAdjustSubmit(payload) {
    try {
      const res = await adjust.run(() => api.post('/financials/forecast-adjustments', payload));
      const data = res.data || res;
      const adjustment = data.adjustment;
      const updatedSummary = data.updatedSummary;
      // Optimistic update of summary totals so the Forecast tile + waterfall react.
      summary.setData?.((prev) => {
        if (!prev?.data?.data?.total) return prev;
        const t = prev.data.data.total;
        return {
          ...prev,
          data: {
            ...prev.data,
            data: {
              ...prev.data.data,
              total: {
                ...t,
                forecastAtCompletionUsdM:
                  Number(t.forecastAtCompletionUsdM || 0) + Number(payload.deltaUsdM),
              },
            },
          },
        };
      });
      // Push the new adjustment to the pending-review rail.
      adjustmentsRes.setData?.((prev) => {
        const current = envelopeList(prev);
        return prev ? { ...prev, data: [adjustment, ...current] } : prev;
      });
      // Optimistic project-card update (the focused project shows a Pending badge).
      projects.setData?.((prev) => {
        const current = envelopeList(prev);
        if (!current.length) return prev;
        return {
          ...prev,
          data: current.map((p) =>
            p.id === payload.projectId
              ? {
                  ...p,
                  financialSummary: {
                    ...(p.financialSummary || {}),
                    ...(updatedSummary || {}),
                    pendingReviewAdjustmentId: adjustment.id,
                  },
                }
              : p,
          ),
        };
      });
      setAdjustOpen(false);
      toast.success({
        title: 'Forecast adjusted',
        body: `${payload.projectId} · Δ $${Number(payload.deltaUsdM).toFixed(1)}M · pending portfolio review`,
      });
      activityRail.push({
        action: 'financial_adjustment.create',
        targetType: 'financial_adjustment',
        targetId: adjustment.id,
        actorUserId: user?.id || 'unknown',
        summary: `${payload.projectId} · Δ $${Number(payload.deltaUsdM).toFixed(1)}M`,
      });
    } catch (e) {
      toast.error({ title: 'Adjustment failed', body: e.message });
    }
  }

  if (summary.loading || portfolio.loading || projects.loading) return <LoadingSkeleton rows={6} />;
  if (accessLimited) {
    return (
      <>
        <Breadcrumbs items={breadcrumbs} />
        <PageActionStrip />
        <section className="bento financials-bento">
          <div className="span-12 card fin-command-header">
            <div>
              <div className="eyebrow">OWHA budget execution</div>
              <h1 className="card-title">Financial Lifecycle Manager</h1>
              <p className="muted">
                Summary-only financial posture for authorized read-only access.
              </p>
            </div>
            <div className="fin-command-header__stats">
              <div>
                <div className="eyebrow">Visibility</div>
                <strong>Summary only</strong>
              </div>
              <div>
                <div className="eyebrow">Source freshness</div>
                <strong>{integrationStatus.summary?.freshnessStatus || 'unknown'}</strong>
              </div>
            </div>
          </div>

          <div className="span-7 card">
            <div className="eyebrow">Access-limited financial summary</div>
            <h2 className="card-title">Governed financial detail is masked</h2>
            <p className="muted">{accessPolicy.message}</p>
            {sourceNotes.length ? (
              <ul className="fin-connector-list">
                {sourceNotes.map((note) => (
                  <li key={`${note.sourceSystem}-${note.freshness}-${note.lastUpdatedAt || 'na'}`}>
                    <div className="row spread">
                      <strong>{note.sourceSystem}</strong>
                      <span className="chip outline sm">{note.freshness || 'current'}</span>
                    </div>
                    <div className="muted small">
                      {note.confidence != null
                        ? `Confidence ${(Number(note.confidence) * 100).toFixed(0)}%`
                        : 'Confidence pending'}
                      {note.lastUpdatedAt ? ` · Updated ${note.lastUpdatedAt}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="No governed financial source notes available" />
            )}
          </div>

          <div className="span-5 card fin-integration-card">
            <header className="row spread">
              <div>
                <div className="eyebrow">Connector readiness</div>
                <h2 className="card-title">FMIS / Tableau integration path</h2>
              </div>
              <span className="chip outline sm">
                {integrationStatus.summary?.readyCount || 0}/
                {integrationStatus.summary?.integrationCount || 0} ready
              </span>
            </header>
            {integration.error ? (
              <ErrorZone
                title="FMIS / Tableau integration status"
                error={integration.error}
                onRetry={integration.refresh}
              />
            ) : (
              <FinancialConnectorEvidenceList
                integrationStatus={integrationStatus}
                emptyTitle="No FMIS/Tableau integration records"
              />
            )}
          </div>
        </section>
      </>
    );
  }

  const focused = focusProjectId ? projectList.find((pr) => pr.id === focusProjectId) : null;

  return (
    <>
      <Breadcrumbs items={breadcrumbs} />
      <PageActionStrip
        actions={
          <>
            <button className="btn ghost tactile-press" onClick={() => setAdjustOpen(true)}>
              Adjust forecast
            </button>
            {scenariosRes.error ? (
              /* AC-STATE-1: a failed scenario fetch is a failure, not an empty
                 state — never mislabel it "No budget-cut scenario loaded". */
              <span className="chip outline sm" data-testid="scenario-fetch-failed">
                Scenario status unavailable
              </span>
            ) : budgetCutScenario ? (
              <button
                className="btn primary tactile-press"
                onClick={runScenarioNow}
                disabled={runScenario.running}
              >
                {runScenario.running
                  ? 'Running scenario…'
                  : budgetCutPercentLabel != null
                    ? `Run ${budgetCutPercentLabel}% budget cut scenario`
                    : `Run ${budgetCutScenario.name || 'budget cut'} scenario`}
              </button>
            ) : (
              <span className="chip outline sm" data-testid="no-scenario-loaded">
                No budget-cut scenario loaded
              </span>
            )}
          </>
        }
      />

      {adjustmentsRes.error ? (
        <ErrorZone
          title="pending forecast adjustments"
          error={adjustmentsRes.error}
          onRetry={adjustmentsRes.refresh}
        />
      ) : null}
      {pendingAdjustments.length ? (
        <section className="fin-pending-rail" data-testid="pending-adjustments-rail">
          <header className="row spread">
            <div>
              <div className="eyebrow">Pending portfolio review</div>
              <h2 className="card-title">
                {pendingAdjustments.length} forecast adjustment(s) awaiting review
              </h2>
            </div>
            <Link className="btn ghost sm" to="/financials/adjustments">
              Open review queue
            </Link>
          </header>
          <ul className="fin-pending-list">
            {pendingAdjustmentPager.items.map((adj) => (
              <li key={adj.id}>
                <div className="row spread">
                  <strong className="mono">{adj.projectId}</strong>
                  <span className={`chip ${Number(adj.deltaUsdM) > 0 ? 'brick' : 'sage'} sm`}>
                    {Number(adj.deltaUsdM) > 0 ? '+' : ''}
                    {Number(adj.deltaUsdM).toFixed(1)} $M
                  </span>
                </div>
                <div className="muted small">{adj.reason}</div>
                <div className="muted small mono">
                  {adj.fundingSource || '—'} ·{' '}
                  {String(adj.reviewStatus || '')
                    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                    .replace(/[_-]+/g, ' ')
                    .toLowerCase()
                    .replace(/^\w/, (c) => c.toUpperCase()) || '—'}
                </div>
                <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
                  <Link className="btn ghost sm" to={`/projects/${adj.projectId}?tab=finance`}>
                    Open project finance
                  </Link>
                </div>
              </li>
            ))}
          </ul>
          <PageViewerControls
            page={pendingAdjustmentPager.page}
            label="forecast adjustments"
            pageSize={pendingAdjustmentPager.pageSize}
            onOffsetChange={pendingAdjustmentPager.setOffset}
            onPageSizeChange={pendingAdjustmentPager.setPageSize}
          />
        </section>
      ) : null}

      {focused ? (
        <AlertCard
          severity="amber"
          title={`Focused on ${focused.name} · ${focused.id}`}
          body={
            returnTo
              ? `Drilled in from ${returnLabel}. Lifecycle column highlighted below.`
              : 'Drilled in from gate-review financial conflict. Lifecycle column highlighted below.'
          }
          actionLabel={returnTo ? `Back to ${returnLabel}` : 'Back to gate review'}
          onAction={() => navigate(returnTo || `/projects/${focused.id}/gate-review/GATE-SG-03`)}
        />
      ) : null}

      <section className="bento financials-bento">
        {summary.error ? (
          /* AC-STATE-1: the summary fetch failing banners its zone (command
             header, KPI tiles, waterfall) while every other zone renders. */
          <div className="span-12">
            <ErrorZone
              title="the financial summary"
              error={summary.error}
              onRetry={summary.refresh}
            />
          </div>
        ) : (
          <>
            <div className="span-12 card fin-command-header">
              <div>
                <div className="eyebrow">OWHA budget execution</div>
                <h1 className="card-title">Financial Lifecycle Manager</h1>
                <p className="muted">
                  Planned, obligated, expended, forecast, labor-hour cost, and source-readiness
                  posture for DHA PRIME leadership review.
                </p>
              </div>
              <div className="fin-command-header__stats">
                <div>
                  <div className="eyebrow">Variance</div>
                  <strong className={`mono ${(total.varianceUsdM || 0) > 0 ? 'brick' : 'sage'}`}>
                    {(total.varianceUsdM || 0) > 0 ? '+' : ''}
                    {(total.varianceUsdM || 0).toFixed(1)} M
                  </strong>
                </div>
                <div>
                  <div className="eyebrow">Burn rate</div>
                  <strong>{burnRatePercent != null ? `${burnRatePercent}%` : '—'}</strong>
                  {burnRatePercent == null ? (
                    <div className="muted small">Burn data not available</div>
                  ) : null}
                </div>
                <div>
                  <div className="eyebrow">Source freshness</div>
                  <strong>{integrationStatus.summary?.freshnessStatus || 'unknown'}</strong>
                </div>
              </div>
            </div>
            <div className="span-3">
              <KpiCard
                id={financialCard('financial-planned')?.id}
                surface="financials"
                label={financialCard('financial-planned')?.label || 'Planned'}
                value={
                  financialCard('financial-planned')?.valueLabel ||
                  format.formatCurrencyMillions((total.plannedBudgetUsdM || 0) * 1_000_000)
                }
                footer={financialCard('financial-planned')?.footer || 'FY26 portfolio total'}
                /* Real payload series or no sparkline (AC-TRUST-2). */
                sparkline={financialCard('financial-planned')?.series}
                sparklineTone={financialCard('financial-planned')?.sparklineTone || 'blue'}
                richTooltip={financialCard('financial-planned')?.richTooltip}
              />
            </div>
            <div className="span-3">
              {/* BL-04: the obligated figure is anchored to its planned
                  denominator (value "of $X planned", delta = obligated−planned)
                  instead of floating next to an unrelated burn percentage.
                  Burn stays visible as secondary context in the footer. */}
              <KpiCard
                id={financialCard('financial-obligated')?.id}
                surface="financials"
                label={financialCard('financial-obligated')?.label || 'Obligated'}
                value={
                  financialCard('financial-obligated')?.valueLabel ||
                  format.formatCurrencyMillions((total.obligatedUsdM || 0) * 1_000_000)
                }
                unit={
                  financialCard('financial-obligated')?.unit ||
                  (financialCard('financial-obligated')?.valueLabel
                    ? undefined
                    : `of ${format.formatCurrencyMillions((total.plannedBudgetUsdM || 0) * 1_000_000)} planned`)
                }
                delta={
                  financialCard('financial-obligated')?.delta ??
                  Number(((total.obligatedUsdM || 0) - (total.plannedBudgetUsdM || 0)).toFixed(1))
                }
                deltaLabel={
                  financialCard('financial-obligated')?.deltaLabel ||
                  `${format.formatCurrencyMillions(
                    Math.abs((total.obligatedUsdM || 0) - (total.plannedBudgetUsdM || 0)) *
                      1_000_000,
                  )} ${(total.obligatedUsdM || 0) > (total.plannedBudgetUsdM || 0) ? 'over' : 'under'} planned`
                }
                deltaTone={
                  financialCard('financial-obligated')?.deltaTone ||
                  ((total.obligatedUsdM || 0) > (total.plannedBudgetUsdM || 0) ? 'amber' : 'slate')
                }
                footer={
                  financialCard('financial-obligated')?.footer ||
                  (burnRatePercent != null ? `${burnRatePercent}% burn rate` : undefined)
                }
                /* Real payload series or no sparkline (AC-TRUST-2). */
                sparkline={financialCard('financial-obligated')?.series}
                sparklineTone={financialCard('financial-obligated')?.sparklineTone || 'amber'}
                richTooltip={financialCard('financial-obligated')?.richTooltip}
              />
            </div>
            <div className="span-3">
              <KpiCard
                id={financialCard('financial-actual')?.id}
                surface="financials"
                label={financialCard('financial-actual')?.label || 'Expended'}
                value={
                  financialCard('financial-actual')?.valueLabel ||
                  format.formatCurrencyMillions((total.actualSpendUsdM || 0) * 1_000_000)
                }
                footer={financialCard('financial-actual')?.footer || 'Cleared, paid invoices'}
                /* Real payload series or no sparkline (AC-TRUST-2). */
                sparkline={financialCard('financial-actual')?.series}
                sparklineTone={financialCard('financial-actual')?.sparklineTone || 'sage'}
                richTooltip={financialCard('financial-actual')?.richTooltip}
              />
            </div>
            <div className="span-3">
              <KpiCard
                id={financialCard('financial-forecast-at-completion')?.id}
                surface="financials"
                label={
                  financialCard('financial-forecast-at-completion')?.label ||
                  'Forecast at completion'
                }
                value={
                  financialCard('financial-forecast-at-completion')?.valueLabel ||
                  format.formatCurrencyMillions((total.forecastAtCompletionUsdM || 0) * 1_000_000)
                }
                delta={
                  financialCard('financial-forecast-at-completion')?.delta ??
                  Math.round(
                    ((total.forecastAtCompletionUsdM || 0) - (total.plannedBudgetUsdM || 0)) * 10,
                  ) / 10
                }
                deltaLabel={
                  financialCard('financial-forecast-at-completion')?.deltaLabel ||
                  `${total.forecastAtCompletionUsdM > total.plannedBudgetUsdM ? '+' : ''}${((total.forecastAtCompletionUsdM || 0) - (total.plannedBudgetUsdM || 0)).toFixed(1)} M variance`
                }
                deltaTone={
                  financialCard('financial-forecast-at-completion')?.deltaTone ||
                  (total.forecastAtCompletionUsdM > total.plannedBudgetUsdM ? 'brick' : 'sage')
                }
                highlight={financialCard('financial-forecast-at-completion')?.highlight ?? true}
                richTooltip={financialCard('financial-forecast-at-completion')?.richTooltip}
              />
            </div>

            <div className="span-7 card fin-waterfall">
              <header className="row spread">
                <div>
                  <div className="eyebrow">Funding flow</div>
                  <h2 className="card-title">
                    Planned → Obligated → Expended → Forecast at completion
                  </h2>
                </div>
              </header>
              <FinancialWaterfall items={waterfallItems} height={340} />
            </div>
          </>
        )}
        <div className="span-5 card fin-funding">
          <header className="row spread">
            <div>
              <div className="eyebrow">Funding source mix</div>
              <h2 className="card-title">Funding source mix by appropriation</h2>
            </div>
          </header>
          {funding.error ? (
            <ErrorZone title="funding sources" error={funding.error} onRetry={funding.refresh} />
          ) : fundingSlices.length ? (
            <InvestmentDonut
              data={fundingSlices}
              centerValue={`$${total.plannedBudgetUsdM?.toFixed(0) || 0}M`}
              centerLabel="planned"
              height={220}
            />
          ) : (
            <EmptyState title="No funding source data" />
          )}
        </div>

        <div className="span-4 card fin-labor-card">
          {labor.error ? (
            <ErrorZone title="labor-hour costs" error={labor.error} onRetry={labor.refresh} />
          ) : (
            <>
              <header className="row spread">
                <div>
                  <div className="eyebrow">Approved labor-hour cost</div>
                  <h2 className="card-title">
                    {format.formatCurrency(laborSummary.laborCostEstimate || 0)}
                  </h2>
                </div>
                <StatusChip status={laborSummary.approvedHours ? 'current' : 'pending'} />
              </header>
              <p className="muted">
                {format.formatNumber(laborSummary.approvedHours || 0)} approved hours are currently
                contributing to financial execution.
              </p>
              {/* W-6 / AC-TIME-5.1: cite the refresh time and link back to the
                  timesheet approvals surface these hours are fed from. */}
              <p className="muted small">
                From approved timesheets
                {labor.loadedAt
                  ? ` · updated ${new Date(labor.loadedAt).toLocaleString()}`
                  : ''} · <Link to="/timesheets">Open timesheet approvals</Link>
              </p>
            </>
          )}
        </div>

        <div className="span-8 card fin-integration-card">
          <header className="row spread">
            <div>
              <div className="eyebrow">Connector readiness</div>
              <h2 className="card-title">FMIS / Tableau integration path</h2>
            </div>
            <span className="chip outline sm">
              {integrationStatus.summary?.readyCount || 0}/
              {integrationStatus.summary?.integrationCount || 0} ready
            </span>
          </header>
          {integration.error ? (
            <ErrorZone
              title="FMIS / Tableau integration status"
              error={integration.error}
              onRetry={integration.refresh}
            />
          ) : (
            <FinancialConnectorEvidenceList
              integrationStatus={integrationStatus}
              emptyTitle="No FMIS/Tableau integration records"
            />
          )}
        </div>

        <div className="span-12 card fin-lifecycle">
          <header className="row spread">
            <div>
              <div className="eyebrow">Portfolio lifecycle matrix</div>
              <h2 className="card-title">Money in flight by stage</h2>
            </div>
            <span className="muted small">Click any project to drill in</span>
          </header>
          {projects.error ? (
            <ErrorZone
              title="the project lifecycle matrix"
              error={projects.error}
              onRetry={projects.refresh}
            />
          ) : (
            <div className="fin-stage-grid">
              {STAGE_COLUMNS.map((c) => (
                <FinancialStageColumn
                  key={c.key}
                  column={c}
                  projects={grouped[c.key]}
                  focusProjectId={focusProjectId}
                  onOpenProject={(projectId) => navigate(`/projects/${projectId}`)}
                  onAdjust={openAdjustForProject}
                />
              ))}
            </div>
          )}
        </div>

        {scenarioResult ? (
          <div className="span-12 card fin-scenario-result">
            <header className="row spread">
              <div>
                <div className="eyebrow">Scenario result</div>
                <h2 className="card-title">{scenarioResult.scenarioId} simulation</h2>
              </div>
              {/* Values below are outputs of the scenario formula engine run
                  against the loaded scenario record — labeled per AC-TRUST-2. */}
              <span className="chip outline sm">Model output · scenario formula engine</span>
            </header>
            <p className="muted">{scenarioResult.summary}</p>
            <div className="fin-scenario-stats">
              <div>
                <div className="eyebrow">Forecast delta</div>
                <strong
                  className={`mono ${scenarioResult.forecastDeltaUsdM > 0 ? 'brick' : 'sage'}`}
                >
                  {scenarioResult.forecastDeltaUsdM > 0 ? '+' : ''}
                  {scenarioResult.forecastDeltaUsdM} M
                </strong>
              </div>
              <div>
                <div className="eyebrow">Risk shift</div>
                <strong>{scenarioResult.riskShift}</strong>
              </div>
              <div>
                <div className="eyebrow">Ran by</div>
                <strong>{scenarioResult.ranByUserId}</strong>
              </div>
            </div>
          </div>
        ) : null}

        <div className="span-12 card fin-allocation">
          <header className="row spread">
            <div>
              <div className="eyebrow">Strategic theme allocation</div>
              <h2 className="card-title">
                {allocationRows.length} portfolios, ${total.plannedBudgetUsdM?.toFixed(1) || 0} M
                planned
              </h2>
            </div>
          </header>
          {allocation.error ? (
            <ErrorZone
              title="strategic theme allocation"
              error={allocation.error}
              onRetry={allocation.refresh}
            />
          ) : allocationRows.length === 0 ? (
            <EmptyState title="No allocation data" />
          ) : (
            <ul className="fin-allocation-list">
              {allocationPager.items.map((row) => (
                <li key={row.portfolioId}>
                  <div className="row spread">
                    <strong>{row.name}</strong>
                    <span className="chip blue sm">
                      ${row.plannedBudgetUsdM?.toFixed(1) || 0} M
                    </span>
                  </div>
                  <div className="muted small">
                    {(row.strategicThemes || []).slice(0, 3).join(' · ')}
                  </div>
                  {/* Bar renders only when the row reports a real mission-value
                      score — no fabricated 60% fill (AC-TRUST-2). */}
                  {typeof row.missionValueScore === 'number' &&
                  !Number.isNaN(row.missionValueScore) ? (
                    <div className="score-bar">
                      <i style={{ width: `${Math.min(100, row.missionValueScore)}%` }} />
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          <PageViewerControls
            page={allocationPager.page}
            label="portfolio allocations"
            pageSize={allocationPager.pageSize}
            onOffsetChange={allocationPager.setOffset}
            onPageSizeChange={allocationPager.setPageSize}
          />
        </div>
      </section>

      <AdjustForecastDrawer
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        onSubmit={handleAdjustSubmit}
        submitting={adjust.running}
        projects={projectList}
        defaults={adjustDefaults}
      />
    </>
  );
}

function humanizeReviewStatus(value) {
  return String(value || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (character) => character.toUpperCase());
}

function humanizeScopeType(value) {
  return String(value || 'project')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (character) => character.toUpperCase());
}

function formatReviewedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

const REVIEW_DECISION_META = {
  approved: {
    label: 'Approve',
    confirmLabel: 'Confirm approval',
    consequence:
      'Approving applies this forecast delta to governed financial posture and records your rationale in the audit trail.',
    tone: 'primary',
  },
  rejected: {
    label: 'Reject',
    confirmLabel: 'Confirm rejection',
    consequence:
      'Rejecting closes this adjustment without applying the forecast delta and records your rationale in the audit trail.',
    tone: 'danger',
  },
  deferred: {
    label: 'Defer',
    confirmLabel: 'Confirm deferral',
    consequence:
      'Deferring returns this adjustment to the queue for a later review cycle and records your rationale in the audit trail.',
    tone: 'primary',
  },
};

export function FinancialAdjustmentsPage() {
  const adjustmentsRes = useApi('/financials/forecast-adjustments', { query: { limit: 50 } });
  const reviewMutation = useMutation();
  const adjustments = useMemo(() => adjustmentsRes.data?.data || [], [adjustmentsRes.data]);
  const [reviewDrafts, setReviewDrafts] = useState({});
  const reviewDraftsRef = useRef({});
  // AC-STATE-3 confirm step + per-row busy tracking (AC-FIN-2.1).
  const [pendingDecision, setPendingDecision] = useState(null); // { adjustmentId, decision }
  const [busyAdjustmentId, setBusyAdjustmentId] = useState(null);

  function requestDecision(adjustmentId, decision) {
    setPendingDecision({ adjustmentId, decision });
  }

  async function reviewAdjustment(adjustmentId, decision) {
    // AC-FIN-2.1: the decision rationale is ALWAYS the reviewer's typed text —
    // never boilerplate autofill. Buttons stay disabled until text is typed,
    // and this guard backstops that rule.
    const decisionRationale = String(
      reviewDraftsRef.current[adjustmentId] ?? reviewDrafts[adjustmentId] ?? '',
    ).trim();
    if (!decisionRationale) {
      toast.error({
        title: 'Rationale required',
        body: 'Type a review rationale before recording an approve, defer, or reject decision.',
      });
      return;
    }
    setBusyAdjustmentId(adjustmentId);
    try {
      const res = await reviewMutation.run(() =>
        api.post(`/financials/forecast-adjustments/${adjustmentId}/review`, {
          decision,
          decisionRationale,
        }),
      );
      adjustmentsRes.setData?.((previous) => {
        const current = envelopeList(previous);
        if (!current.length) return previous;
        return {
          ...previous,
          data: current.map((adjustment) =>
            adjustment.id === adjustmentId
              ? {
                  ...adjustment,
                  ...(res.data?.adjustment || {}),
                  reviewStatus: res.data?.adjustment?.reviewStatus || decision,
                }
              : adjustment,
          ),
        };
      });
      toast.success({
        title: 'Adjustment reviewed',
        body: `${adjustmentId} marked ${decision}.`,
      });
      setReviewDrafts((current) => ({
        ...current,
        [adjustmentId]: res.data?.adjustment?.decisionRationale || decisionRationale,
      }));
      reviewDraftsRef.current = {
        ...reviewDraftsRef.current,
        [adjustmentId]: res.data?.adjustment?.decisionRationale || decisionRationale,
      };
    } catch (error) {
      toast.error({ title: 'Adjustment review failed', body: error.message });
    } finally {
      setBusyAdjustmentId(null);
    }
  }

  // ---- Derived rollups for the hero band + header stats -----------------
  const adjustmentStats = useMemo(() => {
    let pending = 0;
    let increases = 0;
    let decreases = 0;
    let netDeltaUsdM = 0;
    let largest = 0;
    for (const adj of adjustments) {
      const delta = Number(adj.deltaUsdM || 0);
      netDeltaUsdM += delta;
      if (delta > 0) increases += 1;
      else if (delta < 0) decreases += 1;
      if (Math.abs(delta) > Math.abs(largest)) largest = delta;
      if (
        String(adj.reviewStatus || '')
          .toLowerCase()
          .includes('pending')
      )
        pending += 1;
    }
    return {
      total: adjustments.length,
      pending,
      increases,
      decreases,
      netDeltaUsdM,
      largest,
    };
  }, [adjustments]);

  // Signed magnitude series for the InlineScoreCell scale — keeps every
  // per-row bar comparable against the largest absolute delta in the queue.
  const maxAbsDelta = useMemo(
    () => adjustments.reduce((m, a) => Math.max(m, Math.abs(Number(a.deltaUsdM || 0))), 0),
    [adjustments],
  );

  if (adjustmentsRes.loading) return <LoadingSkeleton rows={4} />;
  if (adjustmentsRes.error) return <ErrorState error={adjustmentsRes.error} />;

  const netTone = adjustmentStats.netDeltaUsdM > 0 ? 'brick' : 'sage';
  const heroItems = adjustments.length
    ? [
        {
          id: 'adj-pending',
          label: 'Awaiting review',
          value: adjustmentStats.pending,
          status: adjustmentStats.pending ? 'amber' : 'sage',
          statusLabel: adjustmentStats.pending ? 'Action needed' : 'Clear',
          footer: `${adjustmentStats.total} adjustment(s) in queue`,
          icon: '⏳',
        },
        {
          id: 'adj-net',
          label: 'Net forecast delta',
          value: `${adjustmentStats.netDeltaUsdM > 0 ? '+' : ''}${adjustmentStats.netDeltaUsdM.toFixed(1)}`,
          unit: '$M',
          status: netTone,
          statusLabel: adjustmentStats.netDeltaUsdM > 0 ? 'Increase' : 'Reduction',
          footer: 'Sum of pending + reviewed deltas',
          icon: '∑',
        },
        {
          id: 'adj-increases',
          label: 'Budget increases',
          value: adjustmentStats.increases,
          status: adjustmentStats.increases ? 'brick' : 'slate',
          footer: `${adjustmentStats.decreases} reduction(s)`,
          icon: '▲',
        },
        {
          id: 'adj-largest',
          label: 'Largest single Δ',
          value: `${adjustmentStats.largest > 0 ? '+' : ''}${adjustmentStats.largest.toFixed(1)}`,
          unit: '$M',
          status:
            adjustmentStats.largest > 0 ? 'brick' : adjustmentStats.largest < 0 ? 'sage' : 'slate',
          footer: 'Most material change in queue',
          icon: '◆',
        },
      ]
    : [];

  return (
    <section className="bento financials-bento">
      <div className="span-12 card fin-command-header">
        <div>
          <div className="eyebrow">Review queue</div>
          <h1 className="card-title">Forecast Adjustments</h1>
          <p className="muted">
            Pending and historical forecast adjustments with formal review-state tracking.
          </p>
        </div>
        <div className="fin-command-header__stats">
          <div>
            <div className="eyebrow">In queue</div>
            <strong>{adjustmentStats.total}</strong>
          </div>
          <div className="fin-stat-tone amber">
            <div className="eyebrow">Awaiting review</div>
            <strong>{adjustmentStats.pending}</strong>
          </div>
          <div className={`fin-stat-tone ${netTone}`}>
            <div className="eyebrow">Net delta</div>
            <strong className="mono">
              {adjustmentStats.netDeltaUsdM > 0 ? '+' : ''}
              {adjustmentStats.netDeltaUsdM.toFixed(1)} M
            </strong>
          </div>
        </div>
      </div>

      {heroItems.length ? (
        <div className="span-12">
          <KpiHeroBand eyebrow="Forecast adjustment posture" items={heroItems} columns={4} />
        </div>
      ) : null}

      <div className="span-12 card">
        {adjustments.length === 0 ? (
          <BrandedEmptyState
            tone="sage"
            icon="✓"
            eyebrow="Review queue"
            title="Review queue is clear"
            description="No forecast adjustments are awaiting portfolio review. New adjustments raised from the Financial Lifecycle Manager will appear here for approve, defer, or reject decisions."
          />
        ) : (
          <ul className="fin-adjustment-review-list">
            {adjustments.map((adjustment) => {
              const delta = Number(adjustment.deltaUsdM || 0);
              const deltaTone = delta > 0 ? 'brick' : 'sage';
              const absDelta = Math.abs(delta);
              const isPendingReview = String(adjustment.reviewStatus || '')
                .toLowerCase()
                .includes('pending');
              const rowBusy = busyAdjustmentId === adjustment.id && reviewMutation.running;
              const hasRationale = Boolean(
                String(reviewDrafts[adjustment.id] ?? adjustment.decisionRationale ?? '').trim(),
              );
              const decisionBlocked = rowBusy || reviewMutation.running || !hasRationale;
              return (
                <li key={adjustment.id}>
                  <div className="row spread">
                    <div>
                      <strong>
                        {adjustment.projectName || adjustment.projectId || adjustment.scopeId}
                      </strong>
                      <div className="muted small mono">{adjustment.id}</div>
                    </div>
                    <div className="fin-sat-adj-delta">
                      <div className="fin-sat-adj-delta__bars" aria-hidden="true">
                        <TrendBars
                          values={[0, delta]}
                          tone={deltaTone}
                          ariaLabel={`Signed delta ${delta} million`}
                        />
                      </div>
                      <span
                        className={`fin-sat-adj-delta__value ${deltaTone}`}
                        data-testid={`adjustment-delta-${adjustment.id}`}
                      >
                        {delta > 0 ? '+' : ''}
                        {delta.toFixed(1)} $M
                      </span>
                    </div>
                  </div>
                  {maxAbsDelta > 0 ? (
                    <div className="fin-sat-adj-magnitude">
                      <InlineScoreCell
                        value={Number(absDelta.toFixed(1))}
                        max={Number(maxAbsDelta.toFixed(1)) || 1}
                        tone={deltaTone}
                        suffix=" $M Δ"
                        ariaLabel={`Adjustment magnitude ${absDelta.toFixed(1)} of ${maxAbsDelta.toFixed(1)} million`}
                      />
                    </div>
                  ) : null}
                  <p className="muted">{adjustment.reason || 'No rationale provided.'}</p>
                  <div className="fin-adjustment-meta">
                    <span>
                      <strong>Scope:</strong>{' '}
                      {`${humanizeScopeType(adjustment.scopeType)} · ${adjustment.scopeId || adjustment.projectId || 'Unspecified'}`}
                    </span>
                    <span>
                      <strong>Requester:</strong> {adjustment.createdByUserId || 'Unknown'}
                    </span>
                    {adjustment.reviewedByUserId ? (
                      <span>
                        <strong>Reviewed by:</strong> {adjustment.reviewedByUserId}
                      </span>
                    ) : null}
                    {adjustment.reviewedAt ? (
                      <span>
                        <strong>Reviewed at:</strong> {formatReviewedAt(adjustment.reviewedAt)}
                      </span>
                    ) : null}
                  </div>
                  {String(adjustment.reviewStatus || '')
                    .toLowerCase()
                    .includes('pending') ? (
                    <FormField
                      label="Review rationale"
                      htmlFor={`fin-review-rationale-${adjustment.id}`}
                      hint="Capture the basis for the approve/defer/reject decision before the action is audited."
                    >
                      <Textarea
                        id={`fin-review-rationale-${adjustment.id}`}
                        rows={2}
                        value={reviewDrafts[adjustment.id] ?? adjustment.decisionRationale ?? ''}
                        onChange={(value) => {
                          reviewDraftsRef.current = {
                            ...reviewDraftsRef.current,
                            [adjustment.id]: value,
                          };
                          setReviewDrafts((current) => ({
                            ...current,
                            [adjustment.id]: value,
                          }));
                        }}
                        placeholder="Document the rationale for the financial review decision."
                      />
                    </FormField>
                  ) : null}
                  {adjustment.decisionRationale ? (
                    <p className="muted small">
                      <strong>Decision rationale:</strong> {adjustment.decisionRationale}
                    </p>
                  ) : null}
                  <div className="row spread">
                    <span className="chip outline sm">
                      {humanizeReviewStatus(adjustment.reviewStatus || 'pendingPortfolioReview')}
                    </span>
                    <div className="row" style={{ gap: 8 }}>
                      <Link
                        className="btn ghost sm"
                        to={`/projects/${adjustment.projectId || adjustment.scopeId}?tab=finance`}
                      >
                        Open project finance
                      </Link>
                      {isPendingReview ? (
                        <>
                          <button
                            type="button"
                            className="btn ghost sm"
                            onClick={() => requestDecision(adjustment.id, 'deferred')}
                            disabled={decisionBlocked}
                          >
                            {rowBusy ? 'Working…' : 'Defer adjustment'}
                          </button>
                          <button
                            type="button"
                            className="btn ghost sm"
                            onClick={() => requestDecision(adjustment.id, 'rejected')}
                            disabled={decisionBlocked}
                          >
                            {rowBusy ? 'Working…' : 'Reject adjustment'}
                          </button>
                          <button
                            type="button"
                            className="btn ghost sm"
                            onClick={() => requestDecision(adjustment.id, 'approved')}
                            disabled={decisionBlocked}
                          >
                            {rowBusy ? 'Working…' : 'Approve adjustment'}
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {pendingDecision
        ? (() => {
            const target = adjustments.find((adj) => adj.id === pendingDecision.adjustmentId);
            const meta = REVIEW_DECISION_META[pendingDecision.decision];
            const delta = Number(target?.deltaUsdM || 0);
            return (
              <ConfirmActionDialog
                open
                title={`${meta.label} forecast adjustment ${pendingDecision.adjustmentId}?`}
                body={`${target?.projectId || target?.scopeId || 'This scope'} · ${delta > 0 ? '+' : ''}${delta.toFixed(1)} $M. ${meta.consequence}`}
                confirmLabel={meta.confirmLabel}
                tone={meta.tone}
                busy={reviewMutation.running}
                onCancel={() => setPendingDecision(null)}
                onConfirm={async () => {
                  const { adjustmentId, decision } = pendingDecision;
                  setPendingDecision(null);
                  await reviewAdjustment(adjustmentId, decision);
                }}
              />
            );
          })()
        : null}
    </section>
  );
}

export function FundingSourcesPage() {
  const funding = useApi('/financials/funding-sources');
  const integration = useApi('/integrations/fmis-tableau/status');
  const revalidateMut = useMutation();
  const [revalidatingSource, setRevalidatingSource] = useState(null);
  const accessLimited = Boolean(funding.data?.data?.accessLimited);
  const accessPolicy = funding.data?.data?.accessPolicy || {};
  const fundingSources = useMemo(() => funding.data?.data?.fundingSources || [], [funding.data]);
  const integrationStatus = useMemo(() => integration.data?.data || {}, [integration.data]);

  // AC-FIN-3.1 — re-validate a stale/aging funding source against its
  // connector: records a real sync run server-side and refreshes freshness.
  async function revalidateSource(sourceName) {
    setRevalidatingSource(sourceName);
    try {
      const res = await revalidateMut.run(() =>
        api.post('/financials/funding-sources/revalidate', { source: sourceName }),
      );
      const payload = res?.data || res;
      const nextFreshness = payload?.freshness || 'current';
      funding.setData((prev) => {
        if (!prev?.data?.fundingSources) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            fundingSources: prev.data.fundingSources.map((f) =>
              (f.source || f.fundingType) === sourceName ? { ...f, freshness: nextFreshness } : f,
            ),
          },
        };
      });
      toast.success({
        title: 'Funding source re-validated',
        body: `${sourceName} · freshness ${nextFreshness}${payload?.syncRun?.id ? ` · sync run ${payload.syncRun.id}` : ''}`,
      });
    } catch (e) {
      toast.error({ title: 'Re-validation failed', body: e.message });
    } finally {
      setRevalidatingSource(null);
    }
  }

  const fundingStats = useMemo(() => {
    let totalUsdM = 0;
    const freshnessCounts = { sage: 0, amber: 0, brick: 0, slate: 0, blue: 0 };
    for (const f of fundingSources) {
      totalUsdM += Number(f.amountUsd || f.plannedUsdM || 0);
      freshnessCounts[freshnessTone(f.freshness || f.richTooltip?.trust?.freshness)] += 1;
    }
    return {
      count: fundingSources.length,
      totalUsdM,
      currentCount: freshnessCounts.sage,
      staleCount: freshnessCounts.amber + freshnessCounts.brick,
    };
  }, [fundingSources]);

  const freshnessSegments = useMemo(() => {
    const counts = { sage: 0, amber: 0, brick: 0, slate: 0 };
    for (const f of fundingSources) {
      const t = freshnessTone(f.freshness || f.richTooltip?.trust?.freshness);
      counts[t === 'blue' ? 'slate' : t] += 1;
    }
    return [
      { label: 'Current', count: counts.sage, tone: 'sage' },
      { label: 'Aging', count: counts.amber, tone: 'amber' },
      { label: 'Expired', count: counts.brick, tone: 'brick' },
      { label: 'Unknown', count: counts.slate, tone: 'slate' },
    ].filter((s) => s.count > 0);
  }, [fundingSources]);

  if (funding.loading) return <LoadingSkeleton rows={4} />;
  if (funding.error) return <ErrorState error={funding.error} />;

  const heroItems = fundingSources.length
    ? [
        {
          id: 'fund-lines',
          label: 'Funding lines',
          value: fundingStats.count,
          status: 'blue',
          footer: 'Appropriation sources tracked',
          icon: '▦',
        },
        {
          id: 'fund-total',
          label: 'Planned funding',
          value: accessLimited ? 'Masked' : `$${fundingStats.totalUsdM.toFixed(1)}`,
          unit: accessLimited ? undefined : 'M',
          status: accessLimited ? 'slate' : 'sage',
          statusLabel: accessLimited ? 'Restricted' : undefined,
          footer: 'Across all funding lines',
          icon: '$',
        },
        {
          id: 'fund-current',
          label: 'Current sources',
          value: fundingStats.currentCount,
          status: fundingStats.currentCount === fundingStats.count ? 'sage' : 'amber',
          statusLabel: fundingStats.staleCount ? `${fundingStats.staleCount} aging` : 'All fresh',
          footer: 'Freshness within tolerance',
          icon: '◷',
        },
        {
          id: 'fund-connectors',
          label: 'Connectors ready',
          value: `${integrationStatus.summary?.readyCount || 0}/${integrationStatus.summary?.integrationCount || 0}`,
          status: freshnessTone(integrationStatus.summary?.freshnessStatus),
          footer: 'FMIS / Tableau connectors',
          icon: '⇄',
        },
      ]
    : [];

  return (
    <section className="bento financials-bento">
      <div className="span-12 card fin-command-header">
        <div>
          <div className="eyebrow">Appropriations</div>
          <h1 className="card-title">Funding Sources</h1>
          <p className="muted">
            Funding-line execution with source traceability and connector freshness context.
          </p>
        </div>
        <div className="fin-command-header__stats">
          <div>
            <div className="eyebrow">Funding lines</div>
            <strong>{fundingStats.count}</strong>
          </div>
          <div className="fin-stat-tone sage">
            <div className="eyebrow">Current sources</div>
            <strong>{fundingStats.currentCount}</strong>
          </div>
          <div
            className={`fin-stat-tone ${freshnessTone(integrationStatus.summary?.freshnessStatus)}`}
          >
            <div className="eyebrow">Freshness</div>
            <strong>{integrationStatus.summary?.freshnessStatus || 'unknown'}</strong>
          </div>
        </div>
      </div>

      {heroItems.length ? (
        <div className="span-12">
          <KpiHeroBand eyebrow="Appropriation posture" items={heroItems} columns={4} />
        </div>
      ) : null}

      {freshnessSegments.length ? (
        <div className="span-12 card fin-funding">
          <header className="row spread">
            <div>
              <div className="eyebrow">Funding source mix</div>
              <h2 className="card-title">Funding mix by freshness posture</h2>
            </div>
            {!accessLimited ? (
              <span className="chip blue sm">${fundingStats.totalUsdM.toFixed(1)} M planned</span>
            ) : (
              <span className="chip outline sm">Amounts masked</span>
            )}
          </header>
          <div className="fin-sat-mix">
            <div className="fin-sat-mix__viz">
              <StatusDonutMini
                segments={freshnessSegments}
                size={130}
                totalLabel="sources"
                ariaLabel="Funding sources by freshness"
              />
            </div>
            <div>
              <p className="muted small">
                Freshness posture across {fundingStats.count} appropriation funding line(s).
                {fundingStats.staleCount
                  ? ` ${fundingStats.staleCount} source(s) are aging or expired and should be re-validated against FMIS before the next obligation cycle.`
                  : ' All sources are within freshness tolerance.'}
              </p>
              <ul className="fin-sat-freshness-legend">
                {freshnessSegments.map((seg) => (
                  <li key={seg.label} className={`fin-stat-tone ${seg.tone}`}>
                    <span
                      className={`fin-fresh-chip tone-${seg.tone}`}
                      style={{ '--fresh-tone': FRESH_TONE_VAR[seg.tone] }}
                    >
                      <span className="fin-fresh-chip__dot" />
                      {seg.label}
                    </span>
                    <strong className="mono">{seg.count}</strong>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : null}

      <div className="span-12 card">
        {accessLimited ? <p className="muted">{accessPolicy.message}</p> : null}
        {fundingSources.length === 0 ? (
          <BrandedEmptyState
            tone="sage"
            icon="▦"
            eyebrow="Appropriations"
            title="No funding lines recorded"
            description="No appropriation funding sources are currently tracked for this scope. Imported FMIS / Tableau financial records will populate this list with source traceability and freshness context."
          />
        ) : (
          <ul className="fin-funding-source-list fin-sat-funding-grid">
            {fundingSources.map((source) => {
              const tone = freshnessTone(source.freshness || source.richTooltip?.trust?.freshness);
              return (
                <li
                  key={source.id || source.source}
                  className="fin-sat-funding-card"
                  style={{ '--fresh-tone': FRESH_TONE_VAR[tone] }}
                >
                  <div className="fin-sat-funding-card__head">
                    <strong className="fin-sat-funding-card__name">
                      {source.source || source.fundingType}
                    </strong>
                    <span
                      className={`chip ${accessLimited ? 'outline' : 'blue'} sm fin-sat-funding-card__amount`}
                    >
                      {accessLimited
                        ? 'Masked'
                        : format.formatCurrencyMillions(
                            Number(source.amountUsd || source.plannedUsdM || 0) * 1_000_000,
                          )}
                    </span>
                  </div>
                  <span className={`fin-fresh-chip tone-${tone}`}>
                    <span className="fin-fresh-chip__dot" />
                    {source.freshness || 'current'}
                  </span>
                  <div className="muted small">Freshness · {source.freshness || 'current'}</div>
                  {!accessLimited && (tone === 'amber' || tone === 'brick') ? (
                    <div className="row" style={{ gap: 6 }}>
                      <button
                        type="button"
                        className="btn secondary sm"
                        disabled={revalidateMut.running}
                        onClick={() => revalidateSource(source.source || source.fundingType)}
                      >
                        {revalidatingSource === (source.source || source.fundingType)
                          ? 'Re-validating…'
                          : 'Re-validate'}
                      </button>
                    </div>
                  ) : null}
                  {source.projectId || source.sourceRecordId ? (
                    <div className="muted small mono">
                      {source.projectId ? (
                        <>
                          Project{' '}
                          <Link to={`/projects/${encodeURIComponent(source.projectId)}`}>
                            {source.projectId}
                          </Link>
                          {' · '}
                        </>
                      ) : null}
                      {source.sourceRecordId ? `Source record ${source.sourceRecordId}` : ''}
                    </div>
                  ) : null}
                  {source.representativeFinancialRecord ? (
                    <>
                      <div className="muted small mono">
                        Imported financial record {source.representativeFinancialRecord.recordId} ·{' '}
                        {source.representativeFinancialRecord.lineageRecordCount || 0} lineage
                        record(s)
                      </div>
                      {source.representativeFinancialRecord.sourceRecordId ? (
                        <div className="muted small mono">
                          Financial source record{' '}
                          {source.representativeFinancialRecord.sourceRecordId}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                  <DataSourceBadge
                    source={
                      source.sourceSystem || source.richTooltip?.trust?.sourceSystem || 'PMRT'
                    }
                    freshness={source.freshness || source.richTooltip?.trust?.freshness}
                    confidence={source.confidence ?? source.richTooltip?.trust?.confidence}
                    lineageTargetId={source.representativeFinancialRecord?.recordId || null}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="span-12 card fin-integration-card">
        <header className="row spread">
          <div>
            <div className="eyebrow">Connector readiness</div>
            <h2 className="card-title">FMIS / Tableau integration path</h2>
          </div>
          <span className="chip outline sm">
            {integrationStatus.summary?.readyCount || 0}/
            {integrationStatus.summary?.integrationCount || 0} ready
          </span>
        </header>
        <FinancialConnectorEvidenceList
          integrationStatus={integrationStatus}
          emptyTitle="No FMIS/Tableau integration records"
        />
      </div>
    </section>
  );
}

function FinancialStageColumn({ column, projects, focusProjectId, onOpenProject, onAdjust }) {
  const pager = useClientPagination(projects, {
    initialPageSize: 5,
    resetKey: column.key,
  });
  return (
    <div className={`fin-stage-col tone-${column.tone}`}>
      <header className="row spread">
        <strong>{column.label}</strong>
        <span className="chip outline sm">{projects.length}</span>
      </header>
      <ul>
        {projects.length === 0 ? (
          <li className="muted small">No projects</li>
        ) : (
          pager.items.map((proj) => {
            const planned = proj.financialSummary?.plannedBudgetUsdM ?? 0;
            const actual = proj.financialSummary?.actualSpendUsdM ?? 0;
            const pct = planned ? Math.round((actual / planned) * 100) : 0;
            const isFocus = focusProjectId === proj.id;
            const pending = proj.financialSummary?.pendingReviewAdjustmentId;
            return (
              <li
                key={proj.id}
                className={`fin-stage-item ${isFocus ? 'focused' : ''}`}
                onClick={() => onOpenProject?.(proj.id)}
              >
                <div className="row spread">
                  <strong>{proj.name}</strong>
                  <div className="row" style={{ gap: 4 }}>
                    {pending ? <span className="chip amber sm">Pending review</span> : null}
                    <StatusChip status={proj.status} />
                  </div>
                </div>
                <div className="row" style={{ gap: 4, marginTop: 4 }}>
                  <button
                    className="btn ghost sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAdjust?.(proj.id);
                    }}
                  >
                    Adjust
                  </button>
                </div>
                <div className="muted small mono">{proj.id}</div>
                <div className="fin-money-row">
                  <span>
                    Planned <span className="mono">${planned.toFixed(1)} M</span>
                  </span>
                  <span>
                    Expended <span className="mono">${actual.toFixed(1)} M</span>
                  </span>
                </div>
                <div className="score-bar">
                  <i
                    className={pct > 100 ? 'brick' : pct > 80 ? 'amber' : 'sage'}
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
                <div className="muted small">{pct}% burn</div>
                <DataSourceBadge
                  source={
                    proj.financialSummary?.sourceSystem || proj.dataTrust?.sourceSystem || 'PMRT'
                  }
                  freshness={
                    proj.financialSummary?.dataTrust?.freshness || proj.dataTrust?.freshness
                  }
                  confidence={
                    proj.financialSummary?.dataTrust?.confidence || proj.dataTrust?.confidence
                  }
                />
              </li>
            );
          })
        )}
      </ul>
      <PageViewerControls
        page={pager.page}
        label={`${column.label.toLowerCase()} projects`}
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </div>
  );
}
